import {
  Button,
  Card,
  Grid,
  markedForDeletionStyle,
  MergeInfoBlock,
  pn,
  Stack,
  Typography,
  useTheme,
} from "@compass/core";
import { ContributorBulkMerge, ContributorBulkMergeContributor, ContributorBulkMergeResource } from "generated/openApi";

export const Contributors = (props: {
  contributors: ContributorBulkMergeContributor[];
  resource: ContributorBulkMergeResource;
  mergeToContributorId: number | undefined;
  setMergeContributorId: (id: number) => void;
}) => {
  const properties = props.resource.contributorProperties || [];
  const theme = useTheme();

  const getContributorsCount = (
    contributors: ContributorBulkMerge["contributors"],
    countKey: keyof ContributorBulkMergeContributor,
    currentContributor: ContributorBulkMergeContributor
  ): number => {
    if (!contributors) {
      return 0;
    }

    return contributors.reduce((total: number, contributor) => {
      if (contributor.id !== currentContributor.id) {
        // @ts-ignore
        total += contributor[countKey] || 0;
      }
      return total;
    }, 0);
  };

  return (
    <Stack direction="column" spacing="md">
      <Typography variant="caption">
        Select a contributor to keep. The rest of the contributors will be deleted and their data will be transferred to
        the keeping contributor.
      </Typography>

      {props.contributors.map((contributor) => {
        const keeping = props.mergeToContributorId === contributor.id;

        const markedForDelete = props.mergeToContributorId !== undefined && props.mergeToContributorId > 0 && !keeping;
        const removingSx = markedForDelete ? markedForDeletionStyle(theme) : undefined;

        return (
          <Card
            header={
              <Stack direction="row" alignItems={"center"} spacing="md">
                <Button
                  variant={keeping ? "contained" : "outlined"}
                  onClick={() => props.setMergeContributorId(contributor.id)}
                >
                  {keeping ? "Keeping" : "Keep"}
                </Button>

                <Typography>
                  {contributor.lastName}, {contributor.firstName}
                </Typography>
              </Stack>
            }
          >
            <Grid container spacing="lg" sx={{ ...removingSx }}>
              <Grid item>
                <MergeInfoBlock
                  value={contributor.employmentsCount}
                  label={
                    properties.find((x) => x.propertyName === pn<ContributorBulkMergeContributor>("employmentsCount"))
                      ?.displayName
                  }
                  addCount={
                    props.mergeToContributorId === contributor.id
                      ? getContributorsCount(props.contributors, "employmentsCount", contributor)
                      : undefined
                  }
                />
              </Grid>
              <Grid item>
                <MergeInfoBlock
                  value={contributor.matchesCount}
                  label={
                    properties.find((x) => x.propertyName === pn<ContributorBulkMergeContributor>("matchesCount"))
                      ?.displayName
                  }
                  addCount={
                    props.mergeToContributorId === contributor.id
                      ? getContributorsCount(props.contributors, "matchesCount", contributor)
                      : undefined
                  }
                />
              </Grid>
              <Grid item>
                <MergeInfoBlock
                  value={contributor.nonPersonnelsCount}
                  label={
                    properties.find((x) => x.propertyName === pn<ContributorBulkMergeContributor>("nonPersonnelsCount"))
                      ?.displayName
                  }
                  addCount={
                    props.mergeToContributorId === contributor.id
                      ? getContributorsCount(props.contributors, "nonPersonnelsCount", contributor)
                      : undefined
                  }
                />
              </Grid>
            </Grid>
          </Card>
        );
      })}
    </Stack>
  );
};
